/*
 * fc-nav-more.js: desktop "More" overflow for the inline header menu (priority+ nav).
 *
 * Markup: snippets/fc-nav-more.liquid renders <li data-fc-nav-more> as the last child
 * of the desktop ul.list-menu--inline. Styles: assets/fc-nav-more.css.
 *
 * How it fits the row: the menu ul is flex-wrap: wrap, so "does not fit" means an item
 * wrapped onto a second line. We show everything, and if anything wraps we reveal More
 * and hide trailing top-level <li>s (never the first) until the row is one line again.
 * Hidden items are NOT moved; their entries inside the More panel are un-hidden instead,
 * so the original mega panels (details-disclosure) are never touched.
 *
 * Recomputes on container width changes (ResizeObserver), when label size transitions
 * finish, after web fonts load, and on window load. Below the desktop breakpoint the inline menu is display:none; we
 * then restore every item and hide More, so the mobile drawer is unaffected.
 *
 * Performance (2026-09-26, Lighthouse: this file cost ~1 s of main thread on a phone). On a
 * ~6,000-element page every layout read after a class change is a full style recalc, so:
 * phones skip all measuring (a matchMedia check, no layout read); desktop finds the cut with a
 * binary search (~4 layouts, not one per tab); panel flips read everything first, then write.
 */
(() => {
  'use strict';

  const OVERFLOWED = 'fc-nav-more-overflowed';
  const FLIP = 'fc-nav-more-flip';
  const EDGE = 16; // px kept clear of the viewport edge
  const PANEL_GAP = 6; // px between the row and the dropdown (matches fc-tokens mega panels)
  // The inline menu is display:none below 992px (component-list-menu.css); fc-nav-more.css uses 990.
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  class FcNavMore {
    constructor(more) {
      this.more = more;
      this.list = more.parentElement;
      this.button = more.querySelector('.fc-nav-more__toggle');
      this.panel = more.querySelector('.fc-nav-more__panel');
      this.items = [...this.list.children].filter((li) => li !== more);
      this.entries = new Map(
        [...more.querySelectorAll('[data-fc-more-index]')].map((el) => [Number(el.dataset.fcMoreIndex), el])
      );
      this.headerWrapper = more.closest('.header-wrapper');
      this.lastWidth = -1;
      this.frame = 0;

      this.button.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
      more.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.isOpen()) return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.button.focus();
      });
      more.addEventListener('focusout', (event) => {
        if (this.isOpen() && event.relatedTarget && !more.contains(event.relatedTarget)) this.close();
      });
      document.addEventListener('click', (event) => {
        if (this.isOpen() && !more.contains(event.target)) this.close();
      });
      // A mega panel opening (caret click, Enter, or hover mode) closes More. `toggle`
      // does not bubble, so listen in the capture phase.
      this.list.addEventListener(
        'toggle',
        (event) => {
          const details = event.target;
          if (!(details instanceof HTMLDetailsElement) || !details.open || more.contains(details)) return;
          this.close();
          const li = this.items.find((item) => item.contains(details));
          if (li) requestAnimationFrame(() => this.flipIfNeeded(li, true));
        },
        true
      );

      const container = this.list.closest('.header__inline-menu') || this.list.parentElement;
      if ('ResizeObserver' in window) {
        new ResizeObserver((records) => {
          const width = Math.round(records[0].contentRect.width);
          if (width === this.lastWidth) return; // our own changes only alter height
          this.lastWidth = width;
          this.schedule();
        }).observe(container);
      } else {
        window.addEventListener('resize', () => this.schedule());
      }
      // Crossing the desktop breakpoint: the observed container may keep its width.
      DESKTOP.addEventListener('change', () => {
        this.restored = false;
        this.schedule();
      });
      // Theme labels carry `transition: 0.5s` (all properties), so crossing a breakpoint
      // animates font-size/padding after the width change; re-fit once the sizes settle.
      this.list.addEventListener('transitionend', (event) => {
        if (/^(font|padding|letter-spacing|margin|width)/.test(event.propertyName)) this.schedule();
      });
      if (document.fonts) {
        document.fonts.ready.then(() => this.schedule());
        document.fonts.addEventListener('loadingdone', () => this.schedule());
      }
      window.addEventListener('load', () => this.schedule());
      this.fit();
    }

    schedule() {
      // Phones: once the menu is restored there is nothing to redo on font loads, resizes or
      // transitions, so don't even queue a frame (Lighthouse bills the next restyle to it).
      if (!DESKTOP.matches && this.restored) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.fit());
    }

    isDisplayed(el) {
      return el.getClientRects().length > 0;
    }

    wraps() {
      const visible = [...this.list.children].filter((li) => this.isDisplayed(li));
      if (visible.length < 2) return false;
      const first = visible[0].getBoundingClientRect();
      const limit = first.top + first.height / 2;
      return visible.some((li) => li.getBoundingClientRect().top > limit);
    }

    fit() {
      // Phones and tablets: the inline menu is display:none. Only restore state (writes, no
      // layout reads) so nothing here forces a style recalc.
      if (!DESKTOP.matches || !this.isDisplayed(this.list)) {
        this.items.forEach((li) => li.classList.remove(OVERFLOWED, FLIP));
        this.more.hidden = true;
        if (this.isOpen()) this.close();
        this.syncEntries();
        this.restored = !DESKTOP.matches;
        return;
      }
      this.restored = false;

      const headerSection = this.list.closest('.section-header');
      const heightBefore = headerSection ? headerSection.offsetHeight : 0;

      this.items.forEach((li) => li.classList.remove(OVERFLOWED, FLIP));
      this.more.hidden = true;
      if (this.wraps()) {
        this.more.hidden = false;
        this.hideOverflow();
      }

      this.syncEntries();
      if (this.more.hidden && this.isOpen()) this.close();
      this.flipAll();

      // The sticky header caches its height in --header-height on connect. If our
      // collapse changed that height, refresh the cached value the same way it does.
      if (headerSection && headerSection.offsetHeight !== heightBefore) {
        const root = document.documentElement;
        if (root.style.getPropertyValue('--header-height')) {
          root.style.setProperty('--header-height', `${headerSection.offsetHeight}px`);
        }
      }
    }

    // With More showing, find the most leading tabs that still fit on one line. Whether the
    // row wraps only grows with the number of tabs shown, so a binary search is exact. The
    // first tab never hides. If every tab fits beside More, More was not needed after all.
    hideOverflow() {
      const n = this.items.length;
      const show = (k) => this.items.forEach((li, i) => li.classList.toggle(OVERFLOWED, i >= k));
      let lo = 1;
      let hi = n;
      let best = 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        show(mid);
        if (this.wraps()) hi = mid - 1;
        else {
          best = mid;
          lo = mid + 1;
        }
      }
      show(best);
      if (best === n) this.more.hidden = true;
    }

    // Reveal the More entries for hidden tabs; stamp out an entry's submenu the first time.
    syncEntries() {
      let active = false;
      this.items.forEach((li, i) => {
        const entry = this.entries.get(i + 1);
        if (!entry) return;
        entry.hidden = !li.classList.contains(OVERFLOWED);
        if (entry.hidden) return;
        const tpl = entry.querySelector(':scope > template[data-fc-more-sub]');
        if (tpl) tpl.replaceWith(tpl.content.cloneNode(true));
        if (entry.hasAttribute('data-fc-active')) active = true;
      });
      this.more.classList.toggle('fc-nav-more--active', active);
    }

    // Keep every visible tab's mega panel on screen: read all geometry, then write.
    flipAll() {
      const viewport = document.documentElement.clientWidth;
      this.items
        .filter((li) => !li.classList.contains(OVERFLOWED))
        .map((li) => this.planFlip(li, viewport))
        .forEach((apply) => apply && apply());
    }

    // A closed <details> still reports its panel's layout width in Chromium, so fit() plans
    // every tab; other engines report 0 until the panel opens, so the toggle handler re-runs
    // this for the opened tab.
    flipIfNeeded(li, opened) {
      if (li.classList.contains(OVERFLOWED)) {
        li.classList.remove(FLIP);
        return;
      }
      if (!opened) li.classList.remove(FLIP);
      const apply = this.planFlip(li, document.documentElement.clientWidth);
      if (apply) apply();
    }

    // Reads only; returns the writes to make (or null).
    planFlip(li, viewport) {
      const details = li.querySelector('details');
      const panel = details && details.querySelector(':scope > .header__submenu');
      if (!panel) return null;
      const width = panel.getBoundingClientRect().width;
      if (!width) return null;

      // Wide 3-level panels: fc-nav-more.css detaches them from the label and centres them
      // in the menu row (the no-JS fallback). Centre under the label instead, clamped to
      // the row, so the panel stays next to what was clicked.
      if (getComputedStyle(details).position === 'static') {
        const host = li.closest('desktop-webi-menu');
        if (!host || getComputedStyle(host).position === 'static') return null;
        const row = host.getBoundingClientRect();
        const label = li.getBoundingClientRect();
        const ideal = label.left + label.width / 2 - width / 2 - row.left;
        const left = Math.round(Math.max(0, Math.min(ideal, row.width - width)));
        return () => {
          panel.style.setProperty('left', `${left}px`, 'important');
          panel.style.setProperty('right', 'auto', 'important');
          panel.style.setProperty('margin-inline', '0', 'important');
        };
      }

      // Anchored panels: open leftward when they would cross the viewport's right edge.
      // Complements fc-tokens.css, which flips the last two <li>s by position; More is now
      // the last <li> and overflowed items are display:none.
      const left = li.getBoundingClientRect().left;
      return left + width > viewport - EDGE ? () => li.classList.add(FLIP) : null;
    }

    isOpen() {
      return this.button.getAttribute('aria-expanded') === 'true';
    }

    open() {
      this.list.querySelectorAll('details[open]').forEach((details) => {
        details.removeAttribute('open');
        const summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      });
      const top = this.more.getBoundingClientRect().bottom + PANEL_GAP;
      this.panel.style.maxHeight = `${Math.max(200, window.innerHeight - top - EDGE)}px`;
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.more.classList.add('fc-nav-more--open');
      if (this.headerWrapper) this.headerWrapper.preventHide = true;
    }

    close() {
      this.panel.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.more.classList.remove('fc-nav-more--open');
      if (this.headerWrapper) this.headerWrapper.preventHide = false;
    }
  }

  const init = () => {
    document.querySelectorAll('[data-fc-nav-more]').forEach((more) => {
      if (more.fcNavMore) return;
      more.fcNavMore = new FcNavMore(more);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor re-renders the header section in place.
  document.addEventListener('shopify:section:load', init);
})();
